import { useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useTarefas, usePerfisAtivosTarefas } from "@/hooks/useTarefas";
import {
  TAREFA_PRIORIDADE_COLOR,
  TAREFA_PRIORIDADE_LABEL,
  TAREFA_STATUS_COLOR,
  TAREFA_STATUS_LABEL,
  type Tarefa,
  type TarefaPrioridade,
  type TarefaStatus,
} from "@/types/tarefas";
import { formatPrazoCurto, isPrazoAtrasado } from "./tarefasUtils";
import { Loader2, Search, Users } from "lucide-react";

type Props = {
  apenasMinhas: boolean;
  onOpenTarefa: (tarefa: Tarefa) => void;
};

export function TarefasLista({ apenasMinhas, onOpenTarefa }: Props) {
  const [busca, setBusca] = useState("");
  const [status, setStatus] = useState<string>("all");
  const [prioridade, setPrioridade] = useState<string>("all");
  const [responsavelId, setResponsavelId] = useState<string>("all");
  const [clienteFiltro, setClienteFiltro] = useState<string>("all");
  const { data: perfis = [] } = usePerfisAtivosTarefas();

  const filtro = useMemo(
    () => ({
      busca: busca.trim() || null,
      status: status === "all" ? null : (status as TarefaStatus),
      prioridade: prioridade === "all" ? null : (prioridade as TarefaPrioridade),
      responsavelId: responsavelId === "all" ? null : responsavelId,
      apenasMinhas,
      comCliente:
        clienteFiltro === "com" ? true : clienteFiltro === "sem" ? false : null,
    }),
    [busca, status, prioridade, responsavelId, apenasMinhas, clienteFiltro]
  );

  const { data: tarefas = [], isLoading } = useTarefas(filtro);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20 text-fynix-muted">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative w-full sm:w-64">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-fynix-muted" />
          <Input
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Buscar título"
            className="pl-9 h-10 rounded-full"
          />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-[160px] h-10 rounded-full">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos status</SelectItem>
            {(Object.keys(TAREFA_STATUS_LABEL) as TarefaStatus[]).map((s) => (
              <SelectItem key={s} value={s}>
                {TAREFA_STATUS_LABEL[s]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={prioridade} onValueChange={setPrioridade}>
          <SelectTrigger className="w-[150px] h-10 rounded-full">
            <SelectValue placeholder="Prioridade" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas prioridades</SelectItem>
            {(Object.keys(TAREFA_PRIORIDADE_LABEL) as TarefaPrioridade[]).map((p) => (
              <SelectItem key={p} value={p}>
                {TAREFA_PRIORIDADE_LABEL[p]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={responsavelId} onValueChange={setResponsavelId}>
          <SelectTrigger className="w-[180px] h-10 rounded-full">
            <SelectValue placeholder="Responsável" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos responsáveis</SelectItem>
            {perfis.map((p) => (
              <SelectItem key={p.id} value={p.id}>
                {p.nome}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={clienteFiltro} onValueChange={setClienteFiltro}>
          <SelectTrigger className="w-[160px] h-10 rounded-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Com e sem cliente</SelectItem>
            <SelectItem value="com">Com cliente</SelectItem>
            <SelectItem value="sem">Internas</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="crm-card overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Título</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Prioridade</TableHead>
              <TableHead>Prazo</TableHead>
              <TableHead>Responsável</TableHead>
              <TableHead>Cliente</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {tarefas.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-muted-foreground py-10">
                  Nenhuma tarefa encontrada
                </TableCell>
              </TableRow>
            ) : (
              tarefas.map((t) => {
                const atrasada = isPrazoAtrasado(t);
                const nPart = t.participantes?.length ?? 0;
                return (
                  <TableRow
                    key={t.id}
                    className="cursor-pointer hover:bg-muted/40"
                    onClick={() => onOpenTarefa(t)}
                  >
                    <TableCell className="font-medium max-w-[240px]">
                      <div className="truncate">{t.titulo}</div>
                      {nPart > 0 && (
                        <span className="inline-flex items-center gap-1 text-[11px] text-fynix-muted mt-0.5">
                          <Users className="h-3 w-3" /> +{nPart}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={TAREFA_STATUS_COLOR[t.status]}>
                        {TAREFA_STATUS_LABEL[t.status]}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={TAREFA_PRIORIDADE_COLOR[t.prioridade]}
                      >
                        {TAREFA_PRIORIDADE_LABEL[t.prioridade]}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className={atrasada ? "text-red-600 font-medium" : ""}>
                        {formatPrazoCurto(t.prazo)}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm">
                      {t.responsavel?.nome ?? "—"}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {t.cliente?.nome ?? "Interna"}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
